import { Box, Heading, Text } from '@chakra-ui/react';
import { useColorModeValue } from '@/components/ui/color-mode';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WhatsAppOverview } from '@/features/channels/components/channel-whatsapp-overview';
import { useUsers } from '@/hooks/useUsers';
import { useWidgetUserRole } from '@/widgets/hooks/use-widget-user-role';

// List view from admin components for generic channel management
import ChannelList from './channel-list'; // or list-view ?

export default function ChannelsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const bgColor = useColorModeValue('white', 'gray.800');
  const { currentUser } = useUsers();
  const widgetRole = useWidgetUserRole();
  const roleName = useMemo(
    () => (widgetRole || currentUser?.role_name || '').toLowerCase(),
    [widgetRole, currentUser?.role_name],
  );
  const canCreateChannels = roleName === 'admin';

  // Set active tab based on URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'whatsapp') {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [location]);

  // Update URL hash when tab changes
  const handleTabChange = (index: number) => {
    setActiveTab(index);
    navigate({
      pathname: location.pathname,
      hash: index === 1 ? 'whatsapp' : '',
    });
  };

  const tabs = [
    { id: 0, label: 'All Channels' },
    { id: 1, label: 'WhatsApp' },
  ];

  return (
    <Box w="full" h="100%" display="flex" flexDirection="column" p={0}>
      <Heading size="2xl" mb={0} flexShrink={0} p={4}>
        Channels
      </Heading>

      <Box
        bg={bgColor}
        // borderWidth="1px"
        // borderRadius="md"
        overflow="hidden"
        flex="1"
        display="flex"
        flexDirection="column"
        minHeight={0}
      >
        {/* NavigationTabs-style tab navigation */}
        <Box p={4} flexShrink={0}>
          <Box width="100%" display="flex" justifyContent="start">
            <Box
              bg="gray.100"
              borderRadius="full"
              p={1}
              display="flex"
              gap={1}
              boxShadow="sm"
            >
              {tabs.map((tab) => (
                <Box
                  key={tab.id}
                  as="button"
                  px={4}
                  py={2}
                  position="relative"
                  fontWeight="medium"
                  color={activeTab === tab.id ? 'gray.900' : 'gray.600'}
                  borderRadius="full"
                  transition="all 0.2s ease"
                  cursor="pointer"
                  bg={activeTab === tab.id ? 'white' : 'transparent'}
                  boxShadow={activeTab === tab.id ? 'sm' : 'none'}
                  border="none"
                  outline="none"
                  _hover={{
                    color: activeTab === tab.id ? 'gray.900' : 'gray.800',
                  }}
                  _active={{
                    transform: 'scale(0.98)',
                  }}
                  _focus={{
                    boxShadow: activeTab === tab.id ? 'sm' : 'outline',
                  }}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <Text fontSize="sm">{tab.label}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Tab content */}
        <Box p={4} flex="1" minHeight={0} overflow="hidden">
          {activeTab === 0 && <ChannelList />}
          {activeTab === 1 && (
            <WhatsAppOverview canCreateChannels={canCreateChannels} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
